/**
 * Beam geometry shared by the mining beam, the scanner pick and the salvage cutting beam (plan §6 M2-09).
 * Float64 world metres in, plain numbers out: no three.js, no allocation (D-011, D-014). The origin-to-centre
 * vector is formed first, so large world coordinates keep their precision.
 */

/**
 * Distance along the ray `o + t·d` to where it first enters the sphere (`c`, `r`), or −1 when it misses or
 * the entry lies beyond `maxT`. An origin inside the sphere returns 0 (instant contact). `d` need not be
 * unit length; `t` is in units of `|d|` (metres for a unit direction).
 */
export function raySphere(
  ox: number,
  oy: number,
  oz: number,
  dx: number,
  dy: number,
  dz: number,
  cx: number,
  cy: number,
  cz: number,
  r: number,
  maxT: number,
): number {
  const lx = ox - cx
  const ly = oy - cy
  const lz = oz - cz
  const c = lx * lx + ly * ly + lz * lz - r * r
  if (c <= 0) return 0
  const a = dx * dx + dy * dy + dz * dz
  if (a === 0) return -1
  const b = lx * dx + ly * dy + lz * dz
  if (b >= 0) return -1 // outside and pointing away
  const disc = b * b - a * c
  if (disc < 0) return -1
  const t = (-b - Math.sqrt(disc)) / a
  return t <= maxT ? t : -1
}

/** Parameter of the point on the segment `o + t·d`, `t ∈ [0, maxT]`, closest to `p` (unit `d`: metres). */
export function rayClosestT(
  ox: number,
  oy: number,
  oz: number,
  dx: number,
  dy: number,
  dz: number,
  px: number,
  py: number,
  pz: number,
  maxT: number,
): number {
  const a = dx * dx + dy * dy + dz * dz
  if (a === 0) return 0
  const t = ((px - ox) * dx + (py - oy) * dy + (pz - oz) * dz) / a
  return t < 0 ? 0 : t > maxT ? maxT : t
}

/**
 * Distance from point `p` to the beam segment `o + t·d`, `t ∈ [0, maxT]` (the salvage anchor and spill tests:
 * "the ray passes within `anchorRadius` of the anchor inside beam range").
 */
export function rayPointDistance(
  ox: number,
  oy: number,
  oz: number,
  dx: number,
  dy: number,
  dz: number,
  px: number,
  py: number,
  pz: number,
  maxT: number,
): number {
  const t = rayClosestT(ox, oy, oz, dx, dy, dz, px, py, pz, maxT)
  const ex = ox + dx * t - px
  const ey = oy + dy * t - py
  const ez = oz + dz * t - pz
  return Math.sqrt(ex * ex + ey * ey + ez * ez)
}
